import { AfterViewChecked, AfterViewInit, Component, computed, DestroyRef, ElementRef, input, Input, InputSignal, OnInit, output, OutputEmitterRef, Signal, signal, ViewChild, WritableSignal } from '@angular/core';
import { Router } from '@angular/router';
import { Clipboard } from '@angular/cdk/clipboard';
import { MatDialog } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter, fromEvent, Observable } from 'rxjs';

// models
import {
	AllowedExtensions,
	GroupedChatMessages,
	IChatFileAttachment,
	IChatMessage,
	IChatMessages,
	IChatUser,
	IGroupedChatMessage,
	IMessageGroup,
	IMentionableUsers,
	MediaType,
	SCROLL_NEXT_DISTANCE_PX,
	AUTO_SCROLLING_TIMEOUT_MS,
	MESSAGE_HIGHLIGHT_DURATION_MS,
	MESSAGE_DELETE_TIMEOUT_MS,
	OpenPlanogramAction,
	MessageReaction,
	ScrollRestoreState,
	SafeHtmlInternal,
	TYPING_INDICATOR_TIMEOUT_MS
} from '../../models/chat';
import { IImagesViewModalData, ImagesViewModalComponent } from '../../modals/images-view-modal/images-view-modal.component';

// services
import { Utils } from '../../services/utils.service';
import { SessionService } from '../../services/session.service';
import { ChatService, IMAGE_EXTENSIONS, VIDEO_EXTENSIONS } from '../../services/chat.service';
import { SocketService } from '../../services/socket.service';
import { SnackBarService } from '../../services/snack-bar.service';
import { EmojiPanelSyncService } from '../../services/emoji-panel-sync.service';

// components
import { SHARED_MODULES } from '../../../shared.';
import { UserLogoGenerateComponent } from '../user-logo-generate/user-logo-generate.component';
import { PhIconWrapperComponent } from '../ph-icon-wrapper/ph-icon-wrapper.component';
import { MentionParserPipe } from '../../pipes/mention-parser.pipe';
import { PhTooltipDirective } from '../../directives/ph-tooltip.directive';
import { PhIconComponent } from '../ph-icon/ph-icon.component';
import { LoaderComponent } from '../loader/loader.component';
import { HumanNumberPipe } from '../../pipes/human-number.pipe';
import { PhUserTooltipComponent } from '../ph-user-tooltip/ph-user-tooltip.component';
import { GetLinkPipe } from '../../pipes/get-link.pipe';
import { PhButtonComponent } from '../ph-button/ph-button.component';
import { ContextMenuAction, ContextMenuComponent, ContextMenuItem } from '../context-menu/context-menu.component';
import { PhEmojiPanelComponent } from '../ph-emoji-panel/ph-emoji-panel.component';
import { PhChatTypingIndicatorComponent } from '../ph-chat-typing-indicator/ph-chat-typing-indicator.component';
import { SafeHtml } from '@angular/platform-browser';
import { ConfirmModalComponent } from '../../modals/confirm-modal/confirm-modal.component';
import { IConfirmModalData } from '../../modals/confirm-modal/models/confirmModalData';
import { IConfirmModalAnswer } from '../../modals/confirm-modal/models/confirmModalAnswer';

@Component({
	selector: 'ph-chat-messages',
	templateUrl: './ph-chat-messages.component.html',
	styleUrls: ['./ph-chat-messages.component.scss'],
	providers: [MentionParserPipe],
	imports: [
		...SHARED_MODULES,
		UserLogoGenerateComponent,
		PhIconWrapperComponent,
		MentionParserPipe,
		PhTooltipDirective,
		PhIconComponent,
		LoaderComponent,
		HumanNumberPipe,
		PhUserTooltipComponent,
		GetLinkPipe,
		PhButtonComponent,
		ContextMenuComponent,
		PhEmojiPanelComponent,
		PhChatTypingIndicatorComponent
	]
})
export class PhChatMessagesComponent implements OnInit, AfterViewInit, AfterViewChecked {

	emptySubtitle: InputSignal<string> = input<string>('CHAT.WRITE_FOR_USE');
	emptyTitleKey: InputSignal<string> = input<string>('CHAT.NO_MESSAGES');
	allowPin: InputSignal<boolean> = input<boolean>(true);
	pinnedBarShown: InputSignal<boolean> = input<boolean>(true);
	goToMainChatShown: InputSignal<boolean> = input<boolean>(false);
	scrollReactions: InputSignal<boolean> = input<boolean>(true);
	allowReactions: InputSignal<boolean> = input<boolean>(true);

	private _messages: IChatMessages;
	private _isAutoScrolling: boolean = false;
	private _preloadingMedia: Map<string, HTMLImageElement | HTMLVideoElement> = new Map();
	private _localDeletedMessages: Map<number | string, IChatMessage> = new Map<number | string, IChatMessage>();
	private _pendingDeleteTimers: Map<number | string, ReturnType<typeof setTimeout>> = new Map<number | string, ReturnType<typeof setTimeout>>();
	private _needsScrollToBottom: boolean = false;
	private _pendingScrollBehavior: ScrollBehavior = 'auto';
	private _resizeObserver: ResizeObserver | null = null;
	private _highlightClearTimeoutId: ReturnType<typeof setTimeout> | null = null;
	private _highlightSeq: number = 0;
	private _pendingScrollRestore: ScrollRestoreState | null = null;
	private _mentionListenersInitialized: boolean = false;
	private _scrollRafId: number | null = null;

	initialFirstUnreadMessageId: WritableSignal<number | null> = signal<number | null>(null);
	readonly isInitialFirstUnreadGroup: Signal<(group: IGroupedChatMessage['groups'][0]) => boolean> = computed(
		(): (group: IMessageGroup) => boolean => {
			return (group: IGroupedChatMessage['groups'][0]): boolean => {
				return group.messages.some((m: IChatMessage): boolean => m.id === this.initialFirstUnreadMessageId() || false);
			};
		}
	);

	readonly userId: number;
	groupedMessages: WritableSignal<GroupedChatMessages> = signal<GroupedChatMessages>([]);
	scrollBottomShown: WritableSignal<boolean> = signal<boolean>(false);
	pinningMessages: WritableSignal<Set<number | string>> = signal<Set<number | string>>(new Set<number | string>());
	unreadCount: WritableSignal<number> = signal<number>(0);
	highlightedMessageId: WritableSignal<number> = signal<number>(-1);
	typingUsers: WritableSignal<Array<IChatUser>> = signal<Array<IChatUser>>([]);
	goToMainChat: OutputEmitterRef<number> = output<number>();
	showReadByItem: WritableSignal<boolean> = signal<boolean>(false);

	@Input('messages') set messages(value: IChatMessages) {
		this.setMessages(value);
		this.initMentionTooltipListeners();
	}

	@ViewChild('messagesContainer') messagesContainer!: ElementRef<HTMLDivElement>;
	@ViewChild('tooltipAnchor', { read: PhTooltipDirective }) tooltipAnchor!: PhTooltipDirective;
	@ViewChild('messageContextMenu') messageContextMenu!: ContextMenuComponent<IChatMessage>;

	readonly messageMenuItems: Array<ContextMenuItem<IChatMessage>> = [
		{
			id: 'show_in_chat',
			labelKey: 'CHAT.SHOW_IN_CHAT',
			icon: 'showInChat',
			visible: (): boolean => this.goToMainChatShown()
		},
		{
			id: 'open_planogram',
			labelKey: 'COMMON.PLANOGRAM.VIEW',
			icon: 'readMore',
			visible: (m: IChatMessage): boolean => m.config?.actions?.some((action: OpenPlanogramAction): boolean => action.type === 'open_planogram')
		},
		{
			id: 'reply',
			labelKey: 'CHAT.REPLY',
			icon: 'return',
			disabled: (m: IChatMessage): boolean => m.status === 'pending' || typeof m.id !== 'number'
		},
		{
			id: 'pin',
			labelKey: 'CHAT.PIN',
			icon: 'pin_message',
			visible: (m: IChatMessage): boolean => this.allowPin() && !m.is_pinned,
			disabled: (m: IChatMessage): boolean => typeof m.id !== 'number' || this.pinningMessages().has(m.id)
		},
		{
			id: 'unpin',
			labelKey: 'CHAT.UNPIN',
			icon: 'unpin_message',
			visible: (m: IChatMessage): boolean => this.allowPin() && !!m.is_pinned,
			disabled: (m: IChatMessage): boolean => typeof m.id !== 'number' || this.pinningMessages().has(m.id)
		},
		{
			id: 'edit',
			labelKey: 'CHAT.EDIT_MESSAGE',
			icon: 'edit',
			visible: (m: IChatMessage): boolean =>
				this.isOwnMessage(m) &&
				!m.deleted &&
				m.status !== 'pending' &&
				typeof m.id === 'number' &&
				!this.hasOpenPlanogramAction(m)
		},
		{
			id: 'delete',
			labelKey: 'CHAT.DELETE_MESSAGE',
			icon: 'deletion',
			visible: (m: IChatMessage) =>
				this.isOwnMessage(m) &&
				!m.deleted &&
				m.status !== 'pending' &&
				typeof m.id === 'number' &&
				!this.hasOpenPlanogramAction(m)
		},
		{ id: 'copy', labelKey: 'CHAT.COPY_MESSAGE', icon: 'content_copy' }
	];

	anchorMentionX: number = 0;
	anchorMentionY: number = 0;
	mentionUserData: WritableSignal<IChatUser> = signal<IChatUser>(null);
	loadedAttachmentKeys: WritableSignal<Set<string>> = signal<Set<string>>(new Set());
	failedAttachmentKeys: WritableSignal<Set<string>> = signal<Set<string>>(new Set());
	reactionPickerMessageId: WritableSignal<number | string | null> = signal<number | string | null>(null);
	reactionPickerPosition: WritableSignal<'above' | 'below'> = signal<'above' | 'below'>('below');
	reactionPickerEmojiPanelOpen: WritableSignal<boolean> = signal<boolean>(false);
	reactionPickerOriginElement: WritableSignal<HTMLElement | null> = signal(null);

	readonly reactionEmojis: readonly string[] = ['❤️', '🔥', '😁', '👍', '😢', '😍'];

	constructor(
		private dialog: MatDialog,
		private destroyRef: DestroyRef,
		private translate: TranslateService,
		private sessionService: SessionService,
		public chatService: ChatService,
		private socketService: SocketService,
		private snackBarService: SnackBarService,
		private emojiPanelSync: EmojiPanelSyncService,
		private router: Router,
		private clipboard: Clipboard,
		private mentionParserPipe: MentionParserPipe
	) {
		this.userId = this.sessionService.defaults.user_id ?? -1;

		this.destroyRef.onDestroy((): void => {
			this._preloadingMedia.forEach((element: HTMLImageElement | HTMLVideoElement): void => {
				element.onload = null;
				element.onerror = null;
				if (element instanceof HTMLVideoElement) {
					element.onloadeddata = null;
					element.pause();
					element.src = '';
					element.load();
				} else {
					element.src = '';
				}
			});
			this._preloadingMedia.clear();
		});
	}

	ngOnInit(): void {
		fromEvent<PointerEvent>(document, 'pointerdown', { capture: true })
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((event: PointerEvent): void => this.onGlobalPointerDown(event));

		if (this.scrollReactions()) {
			this.chatService.scrollToMessage$
				.pipe(takeUntilDestroyed(this.destroyRef))
				.subscribe((messageId: number): void => {
					const activeTab: number = this.chatService.activeChatTab();
					const isPinnedView: boolean = this.goToMainChatShown();
					const isActiveView: boolean = isPinnedView ? activeTab === 1 : activeTab === 0;
					if (!isActiveView) return;

					const element: HTMLElement = this.getMessageElement(messageId);
					if (!element) {
						if (this.goToMainChatShown()) return;

						const existsInCurrentData: boolean = this._messages?.results?.some(
							(message: IChatMessage): boolean => message.id === messageId
						) ?? false;
						if (existsInCurrentData) return;

						this.chatService.highlightAnchor.set(true);
						this.chatService.loadAroundMessage$.next(messageId);
					} else {
						this.scrollToMessage(messageId, true);
					}
				});
		}

		this.socketService.chatTyping
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((data: { id: number; full_name: string }): void => {
				if (!data) return;
				if (data.id === this.userId) return;

				const current: Array<IChatUser> = this.typingUsers();
				const filtered: Array<IChatUser> = current.filter((u: IChatUser): boolean => u.id !== data.id);
				const nextUser: IChatUser = {
					id: data.id,
					full_name: data.full_name,
					photo: null
				};

				this.typingUsers.set([...filtered, nextUser]);
				this._scheduleTypingTimeout(data.id);
			});

		this.socketService.chatRestoreMessage
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((response: { message: IChatMessage }): void => {
				const restoredId: number | undefined = response?.message?.id as number | undefined;
				if (typeof restoredId !== 'number') return;

				const timerId: ReturnType<typeof setTimeout> | undefined = this._pendingDeleteTimers.get(restoredId);
				if (timerId) {
					clearTimeout(timerId);
					this._pendingDeleteTimers.delete(restoredId);
				}

				this._localDeletedMessages.delete(restoredId);
				this.chatService.removePendingRestoreMessage(restoredId);
			});
	}

	ngAfterViewInit(): void {
		const firstUnreadId: number | null = this.initialFirstUnreadMessageId();
		if (firstUnreadId != null) {
			setTimeout((): void => this.scrollToMessage(firstUnreadId, false));
		} else if (!this._messages?.previous) {
			this.scrollToBottom();
		}
		this.chatService.inputMessage$
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((): void => this.checkScrollBottom());

		this.chatService.scrollToBottom$
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe(() => setTimeout((): void => this.onScrollBottom('smooth')));

		this.chatService.newMessage$
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((): void => {
				if (this._messages?.previous) return;
				setTimeout((): void => this.onScrollBottom('smooth'));
			});

		this.chatService.checkScrollBottomPosition$
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((): void => this.checkScrollBottom());

		this.socketService.chatUnreadCount
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((data: { unread: number }): void => {
				this.unreadCount.set(data.unread);
			});

		this.initMentionTooltipListeners();

		this._resizeObserver = new ResizeObserver((): void => {
			const container: HTMLDivElement = this.messagesContainer?.nativeElement;
			if (container && container.scrollHeight > 0 && this._needsScrollToBottom) {
				this.scrollToBottom(this._pendingScrollBehavior);
				this._needsScrollToBottom = false;
			}
		});
		this._resizeObserver.observe(this.messagesContainer.nativeElement);
		this.destroyRef.onDestroy((): void => this._resizeObserver?.disconnect());
		this.destroyRef.onDestroy((): void => this._clearHighlight());
	}

	ngAfterViewChecked(): void {
		if (!this._pendingScrollRestore || !this.messagesContainer?.nativeElement) return;
		const container: HTMLDivElement = this.messagesContainer.nativeElement;
		const pending: ScrollRestoreState = this._pendingScrollRestore;
		this._pendingScrollRestore = null;

		const newScrollHeight: number = container.scrollHeight;
		if (newScrollHeight !== pending.oldScrollHeight) {
			container.scrollTop = pending.oldScrollTop + (newScrollHeight - pending.oldScrollHeight);
		}
		requestAnimationFrame((): void => {
			const el: HTMLDivElement = this.messagesContainer?.nativeElement;
			if (!el) return;
			const sh: number = el.scrollHeight;
			el.scrollTop = pending.oldScrollTop + (sh - pending.oldScrollHeight);
		});
	}

	private initMentionTooltipListeners(): void {
		if (this._mentionListenersInitialized || !this.messagesContainer) return;
		this._mentionListenersInitialized = true;

		fromEvent(this.messagesContainer.nativeElement, 'mouseover')
			.pipe(
				takeUntilDestroyed(this.destroyRef),
				filter((event: MouseEvent): boolean => {
					const target: HTMLElement = event.target as HTMLElement;
					return !!target.closest('.mention-tag');
				})
			)
			.subscribe((event: MouseEvent): void => {
				const target: HTMLElement = event.target as HTMLElement;
				const mentionElement: HTMLSpanElement = target.closest('.mention-tag');
				const messageContainer: HTMLElement = mentionElement.closest('.message-container') as HTMLElement;
				const messageId: number = Number(messageContainer?.id.replace('message-', ''));
				const message: IChatMessage = this._messages.results.find((message: IChatMessage): boolean => message.id === messageId);
				const user: IChatUser = message?.mentioned_users?.find((user: IChatUser): boolean => user.id === Number(
					mentionElement.id));
				this.mentionUserData.set(user);
				const rect: DOMRect = mentionElement.getBoundingClientRect();

				this.anchorMentionX = rect.left + rect.width / 2;
				this.anchorMentionY = rect.top;

				if (this.tooltipAnchor) this.tooltipAnchor.show();
			});

		fromEvent(this.messagesContainer.nativeElement, 'mouseout')
			.pipe(
				takeUntilDestroyed(this.destroyRef),
				filter((event: MouseEvent): boolean => {
					const target = event.target as HTMLElement;
					return !!target.closest('.mention-tag');
				})
			)
			.subscribe((): void => {
				if (this.tooltipAnchor) this.tooltipAnchor.hide();
			});
	}

	private groupMessages(messages: Array<IChatMessage>): GroupedChatMessages {
		const today: Date = new Date();
		const yesterday: Date = new Date();
		yesterday.setDate(today.getDate() - 1);

		const todayTranslate: string = this.translate.instant('TODAY');
		const yesterdayTranslate: string = this.translate.instant('YESTERDAY');

		const seenMessageIds: Set<number | string> = new Set<number | string>();

		const sortedMessages: Array<IChatMessage> = [...messages].sort((a: IChatMessage, b: IChatMessage): number => {
				const aTime: number = Utils.parseIsoWithMicro(a.created_at);
				const bTime: number = Utils.parseIsoWithMicro(b.created_at);
				return aTime - bTime;
			});

		const groupedByDate: { [key: string]: Array<IChatMessage> } = sortedMessages
			.reduce((acc: { [key: string]: Array<IChatMessage> }, message: IChatMessage): {
				[key: string]: Array<IChatMessage>
			} => {
				if (message.id != null && seenMessageIds.has(message.id)) {
					return acc;
				}
				if (message.id != null) {
					seenMessageIds.add(message.id);
				}

				const date: string = new Date(message.created_at).toISOString().split('T')[0];
				const messageDate: Date = new Date(date);

				let dateLabel: string;
				if (Utils.isSameDay(messageDate, today)) {
					dateLabel = todayTranslate;
				} else if (Utils.isSameDay(messageDate, yesterday)) {
					dateLabel = yesterdayTranslate;
				} else {
					dateLabel = messageDate.toLocaleDateString('uk');
				}

				if (!acc[dateLabel]) acc[dateLabel] = [];
				acc[dateLabel].push(message);
				return acc;
			}, {} as { [key: string]: Array<IChatMessage> });

		return Object.keys(groupedByDate).map((dateLabel: string): IGroupedChatMessage => {
			const dateMessages: Array<IChatMessage> = groupedByDate[dateLabel];
			const groups: Array<{ user: IChatUser; messages: Array<IChatMessage> }> = [];

			dateMessages.forEach((message: IChatMessage): void => {
				if (this._localDeletedMessages.has(message.id)) {
					message.deleted = true;
					message.content = null;
					if (message.attachments?.length) {
						message.attachments = [];
					}
				}
				if (message.status !== 'failed') {
					if (typeof message.id === 'string') {
						message.status = 'pending';
					} else if (message.read_by?.length) {
						message.status = 'read';
					} else {
						message.status = 'sent';
					}
				}

				message.attachments?.forEach((attachment: IChatFileAttachment, index: number): void => {
					if (!attachment.key) {
						attachment.key = `${message.id}-${index}-${attachment.file ?? attachment.name}`;
					}

					if (!attachment.icon) {
						attachment.icon = this.chatService.getIconForFileExtension(
							attachment.name.split('.').pop().toLowerCase()
						);
					}
					const isImage: boolean = IMAGE_EXTENSIONS.includes(attachment.content_type as AllowedExtensions);
					const isVideo: boolean = VIDEO_EXTENSIONS.includes(attachment.content_type as AllowedExtensions);

					if (isImage || isVideo) {
						if (!attachment.type) {
							attachment.type = isImage ? 'image' : 'video';
						}
						if (!attachment.previewUrl) {
							attachment.previewUrl = attachment.file;
						}

						const key: string = attachment.key;
						if (
							!this.loadedAttachmentKeys().has(key) &&
							!this.failedAttachmentKeys().has(key) &&
							!this._preloadingMedia.has(key)
						) {
							this.preloadAttachment(attachment);
						}
					} else {
						if (!attachment.type) {
							attachment.type = 'file';
						}
					}
				});

				const lastGroup: {
					user: IChatUser;
					messages: Array<IChatMessage>
				} | undefined = groups[groups.length - 1];
				const messageTime: number = new Date(message.created_at).getTime();

				if (lastGroup && lastGroup.user.id === message.user.id) {
					const lastMessageTime: number = new Date(lastGroup.messages[lastGroup.messages.length - 1].created_at).getTime();
					if (messageTime - lastMessageTime <= 600000) {
						lastGroup.messages.push(message);
						return;
					}
				}

				groups.push({
					user: message.user,
					messages: [message]
				});
			});

			return {
				dateLabel,
				groups
			};
		});
	}

	private setMessages(messagesData: IChatMessages): void {
		const container: HTMLDivElement = this.messagesContainer?.nativeElement;
		const oldScrollHeight: number = container?.scrollHeight || 0;
		const oldScrollTop: number = container?.scrollTop || 0;

		this._messages = messagesData;
		if (this.initialFirstUnreadMessageId() == null) {
			const uid: number = this.userId;
			if (uid >= 0) {
				for (const msg of messagesData.results) {
					if (typeof msg.id !== 'number') continue;
					if (msg.user?.id === uid) continue;
					if (msg.deleted) continue;
					const readByMe: boolean = (msg.read_by ?? []).some((u: { id: number }): boolean => u.id === uid);
					if (!readByMe) {
						this.initialFirstUnreadMessageId.set(msg.id);
						break;
					}
				}
			}
		}
		const grouped: GroupedChatMessages = this.groupMessages(messagesData.results);
		this.groupedMessages.set(grouped);
		this._isAutoScrolling = true;

		const focusMessageId: number = this.chatService.focusMessageId();

		if (!Utils.isNullOrUndefined(messagesData.anchor_message_id) && this.chatService.highlightAnchor()) {
			const anchorId: number = messagesData.anchor_message_id;
			messagesData.anchor_message_id = undefined;
			setTimeout((): void => this.scrollToMessage(anchorId, true), 0);
			this.chatService.focusMessageId.set(null);
			this.chatService.highlightAnchor.set(false);
		} else if (
			this.chatService.highlightAnchor() &&
			!Utils.isNullOrUndefined(focusMessageId) &&
			Number.isFinite(Number(focusMessageId)) &&
			messagesData.results?.some((m: IChatMessage): boolean => Number(m.id) === focusMessageId)
		) {
			const targetId: number = Number(focusMessageId);
			setTimeout((): void => this.scrollToMessage(targetId, true), 0);
			this.chatService.focusMessageId.set(null);
			this.chatService.highlightAnchor.set(false);
		} else if (messagesData.anchor_date) {
			const dayIndex: number = this.groupedMessages().findIndex((group: IGroupedChatMessage): boolean =>
				group.groups[0]?.messages[0]?.created_at.includes(messagesData.anchor_date)
			);
			messagesData.anchor_date = undefined;
			if (dayIndex !== -1) setTimeout((): void => this.scrollToDay(dayIndex));
		} else if (this.chatService.isLoadingNext() && container && oldScrollHeight > 0) {
			this._pendingScrollRestore = { oldScrollTop, oldScrollHeight };
		}

		this.chatService.isLoadingPrevious.set(false);
		this.chatService.isLoadingNext.set(false);
		setTimeout((): void => {
			this._isAutoScrolling = false;
			this.loadUntilScrollable();
		}, AUTO_SCROLLING_TIMEOUT_MS);
	}

	private loadUntilScrollable(): void {
		const container: HTMLDivElement = this.messagesContainer?.nativeElement;
		if (!container || this.chatService.isLoadingNext() || this.chatService.isLoadingPrevious()) return;
		if (container.scrollHeight > container.clientHeight) return;
		if (this._messages?.next) {
			this.chatService.loadNextMessages$.next();
		}
	}

	private scrollToBottom(behavior: ScrollBehavior = 'auto'): void {
		const container: HTMLDivElement = this.messagesContainer?.nativeElement;
		if (!container) return;

		if (container.scrollHeight === 0) {
			this._needsScrollToBottom = true;
			this._pendingScrollBehavior = behavior;
			return;
		}

		this._isAutoScrolling = true;
		container.scrollTo({ top: container.scrollHeight, behavior: behavior });
		setTimeout((): boolean => this._isAutoScrolling = false, AUTO_SCROLLING_TIMEOUT_MS);
	}

	private scrollToDay(index: number): void {
		this._isAutoScrolling = true;
		const element: HTMLElement = document.getElementById('day-' + index);
		if (element) element.scrollIntoView({ behavior: 'smooth', block: 'start' });
		setTimeout((): boolean => this._isAutoScrolling = false, AUTO_SCROLLING_TIMEOUT_MS);
	}

	private getMessageElement(messageId: number): HTMLElement | null {
		const container: HTMLDivElement = this.messagesContainer?.nativeElement;
		return container?.querySelector(`[id="message-${messageId}"]`) ?? null;
	}

	public scrollToMessage(messageId: number, highlighted: boolean = false): void {
		this._clearHighlight();
		const highlightSeq: number = this._highlightSeq;
		this._isAutoScrolling = true;

		const element: HTMLElement = this.getMessageElement(messageId);
		const container: HTMLDivElement = this.messagesContainer?.nativeElement;
		const behavior: ScrollBehavior = highlighted ? 'smooth' : 'auto';

		if (highlighted && element) {
			if (!container) {
				this._setHighlight(messageId, highlightSeq);
			} else {
				const observer = new IntersectionObserver(
					(entries: Array<IntersectionObserverEntry>): void => {
						if (entries[0]?.isIntersecting) {
							observer.disconnect();
							this._setHighlight(messageId, highlightSeq);
						}
					},
					{ root: container, threshold: 0.01 }
				);
				observer.observe(element);
				this.destroyRef.onDestroy((): void => observer.disconnect());
			}
		}

		if (element && container) {
			const paddingPx: number = 8;

			const containerRect: DOMRect = container.getBoundingClientRect();
			const elementRect: DOMRect = element.getBoundingClientRect();

			const elementTopRelative: number = elementRect.top - containerRect.top;
			const elementBottomRelative: number = elementTopRelative + elementRect.height;

			const visibleTop: number = paddingPx;
			const visibleBottom: number = container.clientHeight - paddingPx;

			let targetScrollTop: number = container.scrollTop;

			if (elementTopRelative < visibleTop) {
				targetScrollTop -= visibleTop - elementTopRelative;
			} else if (elementBottomRelative > visibleBottom) {
				targetScrollTop += elementBottomRelative - visibleBottom;
			}

			const maxScrollTop: number = Math.max(0, container.scrollHeight - container.clientHeight);
			targetScrollTop = Math.min(maxScrollTop, Math.max(0, targetScrollTop));

			container.scrollTo({ top: targetScrollTop, behavior });
		} else if (element) {
			element.scrollIntoView({ behavior, block: 'center' });
		}

		setTimeout((): boolean => this._isAutoScrolling = false, AUTO_SCROLLING_TIMEOUT_MS);
	}

	private _setHighlight(messageId: number, seq: number): void {
		if (seq !== this._highlightSeq) return;
		if (this._highlightClearTimeoutId != null) {
			clearTimeout(this._highlightClearTimeoutId);
			this._highlightClearTimeoutId = null;
		}
		this.highlightedMessageId.set(messageId);
		this._highlightClearTimeoutId = setTimeout((): void => {
			if (seq !== this._highlightSeq) return;
			this._highlightClearTimeoutId = null;
			this.highlightedMessageId.set(-1);
		}, MESSAGE_HIGHLIGHT_DURATION_MS);
	}

	private _clearHighlight(): void {
		this._highlightSeq += 1;
		if (this._highlightClearTimeoutId != null) {
			clearTimeout(this._highlightClearTimeoutId);
			this._highlightClearTimeoutId = null;
		}
		this.highlightedMessageId.set(-1);
	}

	private _typingTimeouts: Map<number, ReturnType<typeof setTimeout>> = new Map<number, ReturnType<typeof setTimeout>>();

	private _scheduleTypingTimeout(userId: number): void {
		const existing: ReturnType<typeof setTimeout> | undefined = this._typingTimeouts.get(userId);
		if (existing) clearTimeout(existing);
		const timeoutId: ReturnType<typeof setTimeout> = setTimeout((): void => {
			this._typingTimeouts.delete(userId);
			this.typingUsers.update((list: Array<IChatUser>): Array<IChatUser> =>
				list.filter((u: IChatUser): boolean => u.id !== userId)
			);
		}, TYPING_INDICATOR_TIMEOUT_MS);
		this._typingTimeouts.set(userId, timeoutId);
	}

	private _clearTypingTimeouts(): void {
		this._typingTimeouts.forEach((id: ReturnType<typeof setTimeout>): void => clearTimeout(id));
		this._typingTimeouts.clear();
	}

	openHelpdesk(): void {
		const links: Record<string, string> = {
			en: 'https://helpdesk.planohero.com/en/article/chat-web-version-8o1snl/',
			uk: 'https://helpdesk.planohero.com/uk/article/chat-veb-versiya-sxrtg8/',
			ru: 'https://helpdesk.planohero.com/ru/article/chat-veb-versiya-tze0rn/'
		};

		window.open(links[this.translate.getCurrentLang()] ?? links.en, '_blank');
	}

	onMessagesScroll(): void {
		if (this._scrollRafId != null) return;

		this._scrollRafId = requestAnimationFrame((): void => {
			this._scrollRafId = null;

			const messageContainer: HTMLDivElement = this.messagesContainer?.nativeElement;
			if (!messageContainer) return;

			const value: boolean = messageContainer.scrollHeight - messageContainer.scrollTop > messageContainer.clientHeight + SCROLL_NEXT_DISTANCE_PX;
			this.scrollBottomShown.set(!!this._messages?.previous || value);

			if (this._isAutoScrolling) return;

			if (this._messages?.next && !this.chatService.isLoadingNext() && messageContainer.scrollTop <= SCROLL_NEXT_DISTANCE_PX) {
				this.chatService.loadNextMessages$.next();
			}

			const distFromBottom: number = messageContainer.scrollHeight - messageContainer.scrollTop - messageContainer.clientHeight;
			if (this._messages?.previous && !this.chatService.isLoadingPrevious() && distFromBottom <= SCROLL_NEXT_DISTANCE_PX) {
				this.chatService.loadPreviousMessages$.next();
			}
		});
	}

	public checkScrollBottom(): void {
		if (this._isAutoScrolling) return;
		const messageContainer: HTMLDivElement = this.messagesContainer?.nativeElement;
		if (!messageContainer) return;
		const needScrollMessages: boolean = Math.abs(messageContainer.scrollHeight - messageContainer.scrollTop - messageContainer.clientHeight) < 10;
		if (needScrollMessages) setTimeout((): void => this.onScrollBottom());
	}

	public onDaySelect(index: number, date?: string): void {
		if (!index && this._messages.next) {
			this.chatService.scrollToDay$.next(date?.split('T')[0]);
		} else {
			this.scrollToDay(index);
		}
	}

	public onMessageSelect(messageId: number | string): void {
		if (this.goToMainChatShown()) {
			this.goToMainChat.emit(Number(messageId));
			return;
		}
		this.chatService.scrollToMessage$.next(messageId as number);
	}

	onMessageContextMenu(event: MouseEvent, message: IChatMessage): void {
		if (message?.deleted) return;
		this.showReadByItem.set(this.isOwnMessage(message));
		this.messageContextMenu.open(event, message);
	}

	public onReplyMessage(message: IChatMessage): void {
		if (message?.status === 'pending' || typeof message?.id !== 'number') return;
		this.chatService.replyMessage$.next(message);
	}

	toggleReactionPicker(message: IChatMessage): void {
		if (!this.allowReactions() || message?.deleted) return;
		this.emojiPanelSync.closeActive();
		const current: string | number = this.reactionPickerMessageId();
		this.reactionPickerMessageId.set(current === message.id ? null : message.id);
		if (current === message.id) {
			this.reactionPickerEmojiPanelOpen.set(false);
			this.reactionPickerOriginElement.set(null);
		}
		if (current !== message.id) {
			setTimeout((): void => this.updateReactionPickerPosition(message), 0);
		}
	}

	toggleReactionPickerEmojiPanel(event: MouseEvent): void {
		if (!this.allowReactions()) return;
		const el = event?.currentTarget as HTMLElement | undefined;
		if (el) {
			this.reactionPickerOriginElement.set(el);
		}
		this.reactionPickerEmojiPanelOpen.update((open: boolean): boolean => {
			return !open;
		});
	}

	onReactionPickerEmojiPick(emoji: string): void {
		if (!this.allowReactions()) return;
		const messageId: string | number = this.reactionPickerMessageId();
		if (messageId == null) return;
		const message: IChatMessage = this.getMessageById(messageId);
		if (message && !message.deleted) {
			this.onAddReaction(message, emoji);
		}
		this.reactionPickerEmojiPanelOpen.set(false);
		this.reactionPickerOriginElement.set(null);
		this.reactionPickerMessageId.set(null);
	}

	onReactionPickerPanelClose(): void {
		if (!this.allowReactions()) return;
		this.reactionPickerEmojiPanelOpen.set(false);
		this.reactionPickerOriginElement.set(null);
	}

	private getMessageById(id: number | string): IChatMessage | null {
		for (const day of this.groupedMessages()) {
			for (const group of day.groups) {
				const found: IChatMessage = group.messages.find((m: IChatMessage): boolean => m.id === id);
				if (found) return found;
			}
		}
		return null;
	}

	reactionUsersTooltip(userIds: Array<number>, mentionable: Array<IMentionableUsers>): string {
		if (!userIds?.length) return '';
		return userIds
			.map((id: number): string => {
				const u: IMentionableUsers | undefined = mentionable.find((m: IMentionableUsers): boolean => m.id === id);
				return u?.full_name ?? String(id);
			})
			.join(', ');
	}

	private updateReactionPickerPosition(message: IChatMessage): void {
		const container: HTMLDivElement = this.messagesContainer?.nativeElement;
		if (!container) return;

		const messageEl: HTMLElement = container.querySelector<HTMLElement>(`#message-${message.id}`);
		if (!messageEl) return;

		const containerRect: DOMRect = container.getBoundingClientRect();
		const messageRect: DOMRect = messageEl.getBoundingClientRect();
		const containerCenterY: number = containerRect.top + containerRect.height / 2;
		const messageCenterY: number = messageRect.top + messageRect.height / 2;

		this.reactionPickerPosition.set(
			messageCenterY > containerCenterY ? 'above' : 'below'
		);
	}

	onAddReaction(message: IChatMessage, emoji: string): void {
		if (!this.allowReactions()) return;
		const reaction: MessageReaction = message.reactions?.find((r: MessageReaction): boolean => r.emoji === emoji);
		const hasOwn: boolean = reaction?.users?.includes(this.userId);
		const messageId = message.id as number;
		const chatId: number = this.chatService.id;
		if (hasOwn) {
			this.chatService.removeReaction(chatId, messageId, emoji).subscribe({
				next: (): void => {
					this.applyReactionUpdate(message, emoji, false);
					this.reactionPickerMessageId.set(null);
				},
				error: (): void => this.snackBarService.setErrorNotification()
			});
			return;
		}
		this.chatService.addReaction(chatId, messageId, emoji).subscribe({
			next: (): void => {
				this.applyReactionUpdate(message, emoji, true);
				this.reactionPickerMessageId.set(null);
			},
			error: (): void => this.snackBarService.setErrorNotification()
		});
	}

	onReactionClick(message: IChatMessage, emoji: string): void {
		if (!this.allowReactions() || message?.deleted) return;
		const messageId = message.id as number;
		const reaction: MessageReaction = message.reactions?.find((r: MessageReaction): boolean => r.emoji === emoji);
		const hasOwn: boolean = reaction?.users?.includes(this.userId);
		const chatId: number = this.chatService.id;
		if (hasOwn) {
			this.chatService.removeReaction(chatId, messageId, emoji).subscribe({
				next: (): void => this.applyReactionUpdate(message, emoji, false),
				error: (): void => this.snackBarService.setErrorNotification()
			});
		} else {
			this.chatService.addReaction(chatId, messageId, emoji).subscribe({
				next: (): void => this.applyReactionUpdate(message, emoji, true),
				error: (): void => this.snackBarService.setErrorNotification()
			});
		}
	}

	private applyReactionUpdate(message: IChatMessage, emoji: string, add: boolean): void {
		if (message?.deleted) return;
		if (!message.reactions) {
			message.reactions = [];
		}

		let reaction: MessageReaction = message.reactions.find((r: MessageReaction): boolean => r.emoji === emoji);

		if (add) {
			if (!reaction) {
				reaction = { emoji, users: [] };
				message.reactions.push(reaction);
			}

			if (!reaction.users.includes(this.userId)) {
				reaction.users = [...reaction.users, this.userId];
			}
		} else if (reaction) {
			reaction.users = reaction.users.filter((id: number): boolean => id !== this.userId);
			if (reaction.users.length === 0) {
				message.reactions = message.reactions.filter((r: MessageReaction): boolean => r.emoji !== emoji);
			}
		}

		this.groupedMessages.update((): Array<IGroupedChatMessage> => [...this.groupedMessages()]);
	}

	private onGlobalPointerDown(event: PointerEvent): void {
		if (!this.allowReactions()) return;
		const inside: boolean = event.composedPath().some((node: EventTarget): boolean => {
			if (!(node instanceof HTMLElement)) return false;
			return (
				node.classList.contains('reaction-picker') ||
				node.classList.contains('actions-container') ||
				node.tagName === 'PH-EMOJI-PANEL' ||
				node.classList.contains('emoji-dropdown') ||
				node.classList.contains('ph-emoji-panel-overlay')
			);
		});
		if (inside) return;
		this.closeReactionPicker();
	}

	private closeReactionPicker(): void {
		this.reactionPickerMessageId.set(null);
		this.reactionPickerEmojiPanelOpen.set(false);
		this.reactionPickerOriginElement.set(null);
	}

	onMessageMenuAction(action: ContextMenuAction<IChatMessage>): void {
		if (action.id === 'copy') {
			this.copyMessage(action.context);
			return;
		}
		if (action.id === 'reply') {
			this.onReplyMessage(action.context);
			return;
		}
		if (action.id === 'show_in_chat') {
			this.onMessageSelect(action.context.id as number);
			return;
		}
		if (action.id === 'pin') {
			this.togglePinMessage(action.context);
			return;
		}
		if (action.id === 'unpin') {
			this.confirmUnpinMessage(action.context);
			return;
		}
		if (action.id === 'delete') {
			this.deleteMessage(action.context);
			return;
		}
		if (action.id === 'open_planogram') {
			const actionData: OpenPlanogramAction = action.context.config.actions.find((action: OpenPlanogramAction): boolean => action.type === 'open_planogram');
			const url: string = this.router.serializeUrl(
				this.router.createUrlTree(
					this.sessionService.getLink(['stand', 'view', actionData.data.planogram_id]),
					{ queryParams: { planogram: actionData.data.plan_id } }
				)
			);
			window.open(url, '_blank');
			return;
		}
		if (action.id === 'edit') {
			this.editMessage(action.context);
			return;
		}
	}

	editMessage(message: IChatMessage | null): void {
		if (!message || !this.isOwnMessage(message) || this.hasOpenPlanogramAction(message)) return;
		this.chatService.editMessage$.next(message);
	}

	isOwnMessage(message: IChatMessage): boolean {
		if (!message) return false;
		return message.user.id === this.userId;
	}

	private hasOpenPlanogramAction(message: IChatMessage): boolean {
		return message?.config?.actions?.some(
			(action: OpenPlanogramAction): boolean => action.type === 'open_planogram'
		) ?? false;
	}

	private snackHttpError(e: any): void {
		const body = e?.error;
		const messageText = body?.error || body;
		if (messageText) {
			this.snackBarService.setSnackBarNotification({ messageText: String(messageText) });
		} else {
			this.snackBarService.setErrorNotification();
		}
	}

	copyMessage(message: IChatMessage): void {
		if (!message) return;
		const safeHtml: SafeHtml = this.mentionParserPipe.transform(message.content);
		const htmlContent: string = (safeHtml as SafeHtmlInternal).changingThisBreaksApplicationSecurity ?? '';
		const plainText: string = new DOMParser().parseFromString(htmlContent, 'text/html').body.textContent || '';
		if (this.clipboard.copy(plainText)) {
			this.snackBarService.setSnackBarNotification({
				messageText: 'CHAT.MESSAGE_COPIED',
				durationSeconds: 2
			});
		} else {
			this.snackBarService.setErrorNotification();
		}
	}

	public togglePinMessage(message: IChatMessage): void {
		if (!this.allowPin() || typeof message.id !== 'number' || this.pinningMessages().has(message.id)) return;

		this.pinningMessages.update((value: Set<string | number>): Set<string | number> => {
			const next: Set<string | number> = new Set<string | number>(value);
			next.add(message.id);
			return next;
		});
		let request: Observable<void>;
		if (message.is_pinned) {
			request = this.chatService.unpinMessage(this.chatService.id, message.id as number);
		} else {
			request = this.chatService.pinMessage(this.chatService.id, message.id as number);
		}

		request.subscribe({
			next: (): void => {
				this.pinningMessages.update((value: Set<string | number>): Set<string | number> => {
					const next: Set<string | number> = new Set<string | number>(value);
					next.delete(message.id);
					return next;
				});
				message.is_pinned = !message.is_pinned;
				this.chatService.pinnedMessages.update((list: Array<IChatMessage>): Array<IChatMessage> => {
					const current: Array<IChatMessage> = list ?? [];
					const without: Array<IChatMessage> = current.filter((m: IChatMessage): boolean => m.id !== message.id);
					return message.is_pinned ? [...without, message] : without;
				});
			},
			error: (err: unknown): void => {
				this.pinningMessages.update((value: Set<string | number>): Set<string | number> => {
					value.delete(message.id);
					return value;
				});
				this.snackHttpError(err);
			}
		});
	}

	private confirmUnpinMessage(message: IChatMessage): void {
		if (!message?.is_pinned || typeof message.id !== 'number') return;

		this.dialog
			.open<ConfirmModalComponent, IConfirmModalData, IConfirmModalAnswer>(ConfirmModalComponent, {
				width: '450px',
				height: 'auto',
				autoFocus: false,
				backdropClass: 'back-drop',
				data: {
					label: 'CHAT.UNPIN_MESSAGE_CONFIRM',
					twoAnswers: true,
					confirmLabel: 'CHAT.UNPIN',
					cancelLabel: 'COMMON.CANCEL'
				}
			})
			.afterClosed()
			.subscribe((result: IConfirmModalAnswer | undefined): void => {
				if (!result?.answer) return;
				this.togglePinMessage(message);
			});
	}

	public onScrollBottom(behavior: ScrollBehavior = 'auto'): void {
		if (this._messages?.previous) {
			this.chatService.loadLastMessages$.next();
		} else {
			this.scrollToBottom(behavior);
		}
	}

	public canRestoreMessage(message: IChatMessage): boolean {
		if (!message || !this.isOwnMessage(message) || typeof message.id !== 'number') return false;
		if (!message.deleted) return false;
		return this.chatService.pendingRestoreMessageIds().has(message.id);
	}

	public deleteMessage(message: IChatMessage): void {
		if (
			!message ||
			!this.isOwnMessage(message) ||
			message.status === 'pending' ||
			typeof message.id !== 'number' ||
			this.hasOpenPlanogramAction(message)
		) return;

		if (this.chatService.pendingRestoreMessageIds().has(message.id)) return;

		this.dialog
			.open<ConfirmModalComponent, IConfirmModalData, IConfirmModalAnswer>(ConfirmModalComponent, {
				width: '450px',
				height: 'auto',
				autoFocus: false,
				backdropClass: 'back-drop',
				data: {
					label: 'CHAT.DELETE_MESSAGE_CONFIRM_TEXT',
					twoAnswers: true,
					confirmLabel: 'CHAT.DELETE_MESSAGE',
					cancelLabel: 'COMMON.CANCEL'
				}
			})
			.afterClosed()
			.subscribe((result: IConfirmModalAnswer | undefined): void => {
				if (!result?.answer) return;

				const backup: IChatMessage = {
					...message,
					attachments: message.attachments ? [...message.attachments] : []
				};

				message.deleted = true;
				message.content = null;
				if (message.attachments?.length) {
					message.attachments = [];
				}

				if (this._messages?.results?.length) {
					this.groupedMessages.set(this.groupMessages(this._messages.results));
				}

				this.snackBarService.setSnackBarNotification({
					messageText: 'CHAT.MESSAGE_DELETED',
					durationSeconds: 2
				});

				const timerId: ReturnType<typeof setTimeout> = setTimeout((): void => {
					const messageId: number = message.id as number;

					this._pendingDeleteTimers.delete(messageId);
					this._localDeletedMessages.delete(messageId);
					this.chatService.removePendingRestoreMessage(messageId);

					if (this._messages?.results?.length) {
						this.groupedMessages.set(this.groupMessages(this._messages.results));
					}

					this.chatService.pinnedMessages.update((list: Array<IChatMessage>): Array<IChatMessage> => {
						const current: Array<IChatMessage> = list ?? [];
						return current.filter((m: IChatMessage): boolean => m.id !== messageId);
					});
				}, MESSAGE_DELETE_TIMEOUT_MS);

				this._pendingDeleteTimers.set(message.id as number, timerId);
				this.chatService.addPendingRestoreMessage(message.id as number);

				this._localDeletedMessages.set(message.id as number, backup);

				this.chatService.deleteMessage(this.chatService.id, message.id as number).subscribe({
					next: (): void => {
						this._localDeletedMessages.delete(message.id as number);
						this.chatService.reloadAttachments$.next();
					},
					error: (err: unknown): void => {
						clearTimeout(timerId);
						this._pendingDeleteTimers.delete(message.id as number);
						this.chatService.removePendingRestoreMessage(message.id as number);

						const original: IChatMessage = this._localDeletedMessages.get(message.id as number);
						if (original) {
							Object.assign(message, original);
						} else {
							message.deleted = false;
						}
						this._localDeletedMessages.delete(message.id as number);

						if (this._messages?.results?.length) {
							this.groupedMessages.set(this.groupMessages(this._messages.results));
						}

						this.snackHttpError(err);
					}
				});
			});
	}

	public restoreMessage(message: IChatMessage): void {
		if (!message || !this.isOwnMessage(message) || typeof message.id !== 'number') return;
		if (!this.canRestoreMessage(message)) return;
		const messageId: number = message.id;

		this.chatService.restoreMessage(this.chatService.id, messageId).subscribe({
			next: (): void => {
				const timerId: ReturnType<typeof setTimeout> | undefined = this._pendingDeleteTimers.get(message.id);
				if (timerId) {
					clearTimeout(timerId);
					this._pendingDeleteTimers.delete(message.id);
				}
				this._localDeletedMessages.delete(message.id);
				this.chatService.removePendingRestoreMessage(messageId);

				this.chatService.reloadAttachments$.next();

				this.snackBarService.setSnackBarNotification({
					messageText: 'CHAT.MESSAGE_RESTORED',
					durationSeconds: 2
				});
			},
			error: (): void => {
				this.snackBarService.setErrorNotification();
			}
		});
	}

	public markAttachmentLoaded(key: string): void {
		this.loadedAttachmentKeys.update((set: Set<string>): Set<string> => new Set(set).add(key));
	}

	public markAttachmentFailed(key: string): void {
		this.loadedAttachmentKeys.update((set: Set<string>): Set<string> => new Set(set).add(key));
		this.failedAttachmentKeys.update((set: Set<string>): Set<string> => new Set(set).add(key));
	}

	public openImage(file: IChatFileAttachment, message: IChatMessage): void {
		const attachments: Array<IChatFileAttachment> = message?.attachments ?? [];
		const files: Array<IChatFileAttachment> = attachments.filter((item: IChatFileAttachment): string => item.previewUrl);

		this.dialog
			.open<ImagesViewModalComponent, IImagesViewModalData>(ImagesViewModalComponent, {
				width: 'auto',
				autoFocus: false,
				backdropClass: 'back-drop',
				data: {
					images: files.map((file: IChatFileAttachment): string => file.previewUrl),
					types: files.map((file: IChatFileAttachment): MediaType => VIDEO_EXTENSIONS.includes(file.content_type as AllowedExtensions)
						? 'video'
						: 'image'),
					names: files.map((file: IChatFileAttachment): string => file.name),
					usernames: files.map((): string => message.user?.full_name ?? message.user?.email ?? ''),
					dates: files.map((): string => message.created_at),
					index: files.indexOf(file),
					showMainButton: false,
					showSecondaryButton: false
				}
			})
			.afterClosed().subscribe();
	}

	ngOnDestroy(): void {
		this._clearTypingTimeouts();
	}

	downloadFile(file: IChatFileAttachment): void {
		fetch(file.file)
			.then((response: Response): Promise<Blob> => response.blob())
			.then((blob: Blob): void => {
				const blobUrl: string = window.URL.createObjectURL(blob);
				const link: HTMLAnchorElement = document.createElement('a');
				link.href = blobUrl;
				link.download = file.name;
				document.body.appendChild(link);
				link.click();
				document.body.removeChild(link);
				window.URL.revokeObjectURL(blobUrl);
			})
			.catch((): void => this.snackBarService.setErrorNotification());
	}

	private preloadAttachment(attachment: IChatFileAttachment): void {
		const key: string = attachment.key;
		if (attachment.type === 'image') {
			const img: HTMLImageElement = new Image();
			img.onload = (): void => {
				this._preloadingMedia.delete(key);
				this.markAttachmentLoaded(key);
			};
			img.onerror = (): void => {
				this._preloadingMedia.delete(key);
				this.markAttachmentFailed(key);
			};
			this._preloadingMedia.set(key, img);
			img.src = attachment.previewUrl;
		} else if (attachment.type === 'video') {
			const video: HTMLVideoElement = document.createElement('video');
			video.onloadeddata = (): void => {
				this._preloadingMedia.delete(key);
				this.markAttachmentLoaded(key);
			};
			video.onerror = (): void => {
				this._preloadingMedia.delete(key);
				this.markAttachmentFailed(key);
			};
			this._preloadingMedia.set(key, video);
			video.src = attachment.previewUrl;
			video.load();
		}
	}
}